import { useState, useCallback, useRef } from 'react';

export interface NormalizedAuthor {
  id: number;
  ppmaId?: number;
  displayName: string;
  email: string;
}

export interface UseAuthorSelectionResult {
  authors: NormalizedAuthor[];
  loading: boolean;
  authorSelectionEnabled: boolean;
  /** Post types the author picker applies to (config-driven; `post` and `page` by default). */
  authorSelectionPostTypes: string[];
  /** True once the authors endpoint answered successfully: it only does so when the customer
   *  has WordPress credentials configured, so this is a customer-level "is WordPress" signal
   *  that does not depend on the page's own (lazily loaded) editor type. */
  wpConfigured: boolean;
  selectedAuthorId: number | null;
  selectedAuthorIdRef: React.MutableRefObject<number | null>;
  setSelectedAuthorId: (id: number | null) => void;
  fetchAuthors: (cmsPageId?: string | null, userEmail?: string | null, userName?: string | null) => Promise<void>;
}

// Same default the API applies: core types only, custom post types (PSEO) opt in via config.
const DEFAULT_AUTHOR_SELECTION_POST_TYPES = ['post', 'page'];

export function useAuthorSelection(): UseAuthorSelectionResult {
  const [authors, setAuthors] = useState<NormalizedAuthor[]>([]);
  const [loading, setLoading] = useState(false);
  const [authorSelectionEnabled, setAuthorSelectionEnabled] = useState(false);
  const [authorSelectionPostTypes, setAuthorSelectionPostTypes] = useState<string[]>(DEFAULT_AUTHOR_SELECTION_POST_TYPES);
  const [wpConfigured, setWpConfigured] = useState(false);
  const [selectedAuthorId, _setSelectedAuthorId] = useState<number | null>(null);
  const selectedAuthorIdRef = useRef<number | null>(null);

  const setSelectedAuthorId = useCallback((id: number | null) => {
    selectedAuthorIdRef.current = id;
    _setSelectedAuthorId(id);
  }, []);

  const fetchAuthors = useCallback(
    async (cmsPageId?: string | null, userEmail?: string | null, userName?: string | null) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (cmsPageId && cmsPageId !== '0') {
          params.set('postId', cmsPageId);
        }
        const qs = params.toString();
        const url = `/api/gutenberg/fetch-authors${qs ? `?${qs}` : ''}`;
        const resp = await fetch(url);
        if (!resp.ok) {
          setWpConfigured(false);
          return;
        }
        const data = await resp.json();
        setWpConfigured(true);
        const authorList: NormalizedAuthor[] = data.authors || [];
        setAuthors(authorList);
        setAuthorSelectionEnabled(data.authorSelectionEnabled ?? false);
        setAuthorSelectionPostTypes(
          Array.isArray(data.authorSelectionPostTypes) && data.authorSelectionPostTypes.length
            ? data.authorSelectionPostTypes
            : DEFAULT_AUTHOR_SELECTION_POST_TYPES
        );
        if (data.currentPostAuthorId != null) {
          setSelectedAuthorId(data.currentPostAuthorId);
        } else {
          // Try email match first, then fall back to display name match
          const match =
            (userEmail && authorList.find((a) => a.email && a.email.toLowerCase() === userEmail.toLowerCase())) ||
            (userName && authorList.find((a) => a.displayName.toLowerCase() === userName.toLowerCase()));
          if (match) {
            setSelectedAuthorId(match.ppmaId ?? match.id);
          } else if (
            (data.authorSelectionEnabled ?? false) &&
            authorList.length > 0 &&
            // NEW drafts only. An EXISTING post whose author lookup failed (non-2xx, or an author
            // missing from the roster) must stay unselected — defaulting here would silently
            // reassign that post's byline on the next upload.
            (!cmsPageId || cmsPageId === '0')
          ) {
            // A new draft must never sit in an authorless state: an empty picker left the preview
            // with no author section at all (no real article looks like that), and the Upload
            // button disabled. Default to the roster's first author — the picker shows it, the
            // preview renders it, and the upload applies that same author, so all three agree.
            const first = authorList[0];
            setSelectedAuthorId(first.ppmaId ?? first.id);
          }
        }
      } catch (err) {
        console.error('[useAuthorSelection] Error fetching authors:', err);
      } finally {
        setLoading(false);
      }
    },
    [setSelectedAuthorId]
  );

  return {
    authors,
    loading,
    authorSelectionEnabled,
    authorSelectionPostTypes,
    wpConfigured,
    selectedAuthorId,
    selectedAuthorIdRef,
    setSelectedAuthorId,
    fetchAuthors,
  };
}
